import React, { useState, useEffect } from 'react';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Users, Loader2, RefreshCw } from 'lucide-react';

export const CustomersPage = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        billing_address: '',
        service_address: '',
        notes: ''
    });

    useEffect(() => {
        loadCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadCustomers = async () => {
        try {
            const res = await getCustomers();
            setCustomers(res.data);
        } catch (err) {
            toast.error('Fehler beim Laden der Kunden');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await loadCustomers();
            toast.success('Aktualisiert');
        } catch (err) {
            toast.error('Fehler beim Aktualisieren');
        } finally {
            setRefreshing(false);
        }
    };

    const openDialog = (customer = null) => {
        if (customer) {
            setSelectedCustomer(customer);
            setFormData({
                name: customer.name || '',
                email: customer.email || '',
                phone: customer.phone || '',
                billing_address: customer.billing_address || '',
                service_address: customer.service_address || '',
                notes: customer.notes || ''
            });
        } else {
            setSelectedCustomer(null);
            setFormData({
                name: '',
                email: '',
                phone: '',
                billing_address: '',
                service_address: '',
                notes: ''
            });
        }
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        
        // Clean up empty strings to null for optional fields
        const cleanedData = {
            ...formData,
            email: formData.email?.trim() || null,
            phone: formData.phone?.trim() || null,
            service_address: formData.service_address?.trim() || null,
            notes: formData.notes?.trim() || null,
        };
        
        try {
            if (selectedCustomer) {
                await updateCustomer(selectedCustomer.id, cleanedData);
                toast.success('Kunde aktualisiert');
            } else {
                await createCustomer(cleanedData);
                toast.success('Kunde erstellt');
            }
            setDialogOpen(false);
            loadCustomers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteCustomer(selectedCustomer.id);
            toast.success('Kunde gelöscht');
            setDeleteDialogOpen(false);
            loadCustomers();
        } catch (err) {
            toast.error('Fehler beim Löschen');
        }
    };

    const filteredCustomers = customers.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="customers-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                        Kunden
                        <button 
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="p-2 rounded-full hover:bg-muted transition-colors"
                            title="Aktualisieren"
                        >
                            <RefreshCw size={20} className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </h1>
                    <p className="text-muted-foreground">{customers.length} Kunden insgesamt</p>
                </div>
                <Button className="btn-scale" onClick={() => openDialog()} data-testid="new-customer-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Neuer Kunde
                </Button>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Kunden suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 input-focus"
                    data-testid="search-input"
                />
            </div>

            {/* Customer Table */}
            <Card>
                <CardContent className="p-0">
                    {filteredCustomers.length === 0 ? (
                        <div className="empty-state py-16">
                            <Users className="h-12 w-12 text-muted-foreground mb-4" />
                            <p className="text-lg font-medium">Keine Kunden gefunden</p>
                            <p className="text-muted-foreground">Erstellen Sie Ihren ersten Kunden</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead className="hide-mobile">E-Mail</TableHead>
                                    <TableHead className="hide-mobile">Telefon</TableHead>
                                    <TableHead className="hide-mobile">Adresse</TableHead>
                                    <TableHead className="w-[100px]">Aktionen</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredCustomers.map((customer) => (
                                    <TableRow key={customer.id} className="table-row-hover" data-testid={`customer-row-${customer.id}`}>
                                        <TableCell>
                                            <div>
                                                <p className="font-medium">{customer.name}</p>
                                                <p className="text-sm text-muted-foreground md:hidden">{customer.email}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell className="hide-mobile">{customer.email || '-'}</TableCell>
                                        <TableCell className="hide-mobile">{customer.phone || '-'}</TableCell>
                                        <TableCell className="hide-mobile max-w-[200px] truncate">{customer.billing_address}</TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openDialog(customer)}
                                                    data-testid={`edit-customer-${customer.id}`}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => { setSelectedCustomer(customer); setDeleteDialogOpen(true); }}
                                                    data-testid={`delete-customer-${customer.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg" data-testid="customer-dialog">
                    <DialogHeader>
                        <DialogTitle>{selectedCustomer ? 'Kunde bearbeiten' : 'Neuer Kunde'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Name *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required
                                data-testid="customer-name-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">E-Mail</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    data-testid="customer-email-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone">Telefon</Label>
                                <Input
                                    id="phone"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                    data-testid="customer-phone-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="billing_address">Rechnungsadresse *</Label>
                            <Textarea
                                id="billing_address"
                                value={formData.billing_address}
                                onChange={(e) => setFormData({...formData, billing_address: e.target.value})}
                                required
                                rows={2}
                                data-testid="customer-billing-address-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="service_address">Leistungsadresse (optional)</Label>
                            <Textarea
                                id="service_address"
                                value={formData.service_address}
                                onChange={(e) => setFormData({...formData, service_address: e.target.value})}
                                rows={2}
                                data-testid="customer-service-address-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="notes">Notizen</Label>
                            <Textarea
                                id="notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                rows={2}
                                data-testid="customer-notes-input"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving} data-testid="save-customer-btn">
                                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                Speichern
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent data-testid="delete-customer-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Kunde löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie den Kunden "{selectedCustomer?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-btn">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default CustomersPage;
